import { useCallback, useEffect, useState } from 'react';
import { useNotify, useRecordContext, useRefresh } from 'react-admin';
import { Box, Button, TextField } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import {
  MIN_DECISION_REASON_LENGTH,
  acknowledgeTmsAlerts,
  confirmTmsCancellation,
  dismissTmsCancellation,
  fetchTmsAlerts,
  toErrorMessage,
  type TmsAlert,
} from './orderActionsApi';
import { ALERT_BUTTON_SX, OrderAlertCard, type OrderAlertLine } from './OrderAlertCard';
import { asSentence, capitalize, splitOutsideParens, startsLowercase } from './alertText';
import { vehicleChangeAlertText } from '../fleet/carChanges';
import { useFeature } from '../featureFlags/FeatureGate';

type Decision = 'confirm' | 'dismiss';

/**
 * Zmiany w TMS po obsadzeniu — alert na górze karty zlecenia (warsztat
 * Transporeon 29.09.2026).
 *
 * Klient potrafi po potwierdzeniu zlecenia dopisać punkt, przesunąć
 * godzinę, zmienić wagę albo zdjąć obsadzenie. Import to wykrywa
 * (`apps/api/src/via/post-assignment-changes.ts`), a tu planista widzi, co
 * czeka na jego uwagę. „Przyjmuję do wiadomości” (do 01.10.2026
 * „Potwierdzam, widziałem”) zapisuje wyłącznie wpis w dzienniku —
 * obsadzenie, warianty i harmonogram auta zostają, decyzja o przeplanowaniu
 * należy do człowieka.
 *
 * Przy podejrzeniu anulacji planista ma dwie jawne decyzje, obie z powodem
 * (30.09.2026; PM: „jeśli człowiek ręcznie potwierdzi anulację, to wtedy
 * przeprojektowujemy grafik pojazdu”): „Potwierdzam anulację” zdejmuje
 * obsadzenie i warianty i zamyka zlecenie (do TMS nic nie idzie), „To nie
 * anulacja” tylko to zapisuje — klient bywa, że anuluje omyłkowo. Zwykła
 * zmiana po obsadzeniu ma samo „Przyjmuję do wiadomości”: przeplanowanie to
 * normalna praca planisty.
 *
 * Przegląd przed prezentacją 02.10.2026: wspólny wygląd z alertem
 * o przeplanowaniu (`OrderAlertCard`); przyciski w stopce, po prawej,
 * „Przyjmuję do wiadomości” zawsze ostatni. Tekst zdjętego obsadzenia sam
 * mówi „W TMS zdjęto obsadzenie…”, więc bez dawnego „Zdjęte obsadzenie: ”
 * przed nim. Przy decyzji o anulacji pole powodu stoi pod alertami, a stopka
 * mówi, co się stanie po zapisie. Działanie bez zmian.
 *
 * Decyzja użytkownika 12.10.2026 („B4 — wyróżnij te ważniejsze alerty”,
 * „B3 — alert o zmianie auta ma być nieco bardziej widoczny”): każdy alert
 * z TMS jest ważny (`apps/api/src/user-offers/alert-importance.ts`) — karta
 * zawsze czerwona i stoi nad alertem o przeplanowaniu. Doszła zmiana
 * pojazdu w TMS po obsadzeniu (`vehicleChange`): naczepa, eksploatacja,
 * dostępność obsadzonego pojazdu albo wariantu.
 *
 * Odpowiedź Marathonu/Euvic 16.10.2026 (pyt. 41), przełącznik
 * `tmsCancellations` (19.10.2026): TMS podaje listę anulowanych zleceń.
 * Wpis „Zlecenie anulowane w TMS” to ten sam rodzaj alertu co podejrzenie
 * (`cancellation`, `confirmedByTms`) — nagłówek „Anulowane w TMS — do
 * decyzji”, te same dwie decyzje; podejrzenie potwierdzone przez TMS dostaje
 * dopisek. Nic nie dzieje się samo.
 */

/** Dymek „Skąd ten alert?” jak na produkcji — zmiany zlecenia w TMS. */
const TMS_ALERT_ORIGIN_BASE =
  'Przy każdym imporcie z TMS porównujemy zlecenia obsadzone naszym pojazdem lub mające ' +
  'aktywne warianty z ich poprzednim stanem. Alert pojawia się, gdy po obsadzeniu zmienią ' +
  'się w TMS punkty trasy, terminy, waga lub LDM, a także gdy w TMS usunięto przypisanie ' +
  'pojazdu. TMS nie oznacza anulacji wprost, dlatego usunięte przypisanie trzeba sprawdzić ' +
  'u zleceniodawcy.';
/** Zmiana pojazdu w kartotece TMS (przełącznik `vehicleChangesFromTms`). */
const TMS_ALERT_ORIGIN_VEHICLE =
  ' Alert pojawia się też, gdy sam pojazd zmieni się w kartotece TMS: ' +
  'naczepa, eksploatacja lub dostępność.';
/** Inny pojazd w TMS niż u nas (przełącznik `vehicleSwapFromTms`). */
const TMS_ALERT_ORIGIN_SWAP =
  ' Pojawia się również, gdy w TMS przy zleceniu stoi ' +
  'inny pojazd niż obsadzony u nas, a import nie przepiął go sam (pojazd wybrany w aplikacji ' +
  'albo spoza naszej floty).';

/**
 * Lista anulowanych w TMS (przełącznik `tmsCancellations`; odpowiedź
 * Marathonu/Euvic 16.10.2026, pyt. 41) — anulacja podana wprost.
 */
const TMS_ALERT_ORIGIN_CANCELLED =
  ' Gdy TMS poda zlecenie na liście anulowanych, alert mówi to wprost: zlecenie anulowane w TMS.';

/**
 * Dymek „Skąd ten alert?” — reguła: `apps/api/src/via/post-assignment-changes.ts`.
 * Zdania o alertach z funkcji „do decyzji” tylko przy ich przełącznikach
 * (12.10.2026); bez nich — tekst z produkcji.
 */
export function tmsAlertOrigin(on: {
  vehicleChanges: boolean;
  vehicleSwap: boolean;
  cancellations?: boolean;
}): string {
  return (
    TMS_ALERT_ORIGIN_BASE +
    (on.vehicleChanges ? TMS_ALERT_ORIGIN_VEHICLE : '') +
    (on.vehicleSwap ? TMS_ALERT_ORIGIN_SWAP : '') +
    (on.cancellations ? TMS_ALERT_ORIGIN_CANCELLED : '')
  );
}

/** Dymek z obiema funkcjami (develop) — karta pojazdu i testy. */
export const TMS_ALERT_ORIGIN = tmsAlertOrigin({ vehicleChanges: true, vehicleSwap: true });

/** Nagłówek karty, gdy TMS sam podał anulację (lista anulowanych, 19.10.2026). */
export const TMS_CANCELLED_TITLE = 'Anulowane w TMS — do decyzji';

/**
 * Dopisek przy podejrzeniu anulacji, które TMS potwierdził listą anulowanych
 * (19.10.2026) — fakt z TMS, decyzja nadal należy do planisty.
 */
export const TMS_CONFIRMED_NOTE = 'TMS potwierdził anulację — zlecenie jest na liście anulowanych.';

/** Nagłówki alertów z Transporeonu (04.11.2026, przełącznik `transporeonImport`). */
export const TRANSPOREON_CANCELLED_TITLE = 'Anulowane w Transporeonie — do decyzji';
export const TRANSPOREON_CHANGE_TITLE = 'Zmiana w Transporeonie';

/** Dymek „Skąd ten alert?” — zdanie o alertach z Transporeonu. */
export const TRANSPOREON_ALERT_ORIGIN =
  ' Alerty z Transporeonu: komunikaty z kolejki zleceniodawcy porównujemy z poprzednim stanem transportu — zmiana okna załadunku lub rozładunku, miejsca, trasy albo wagi, anulacja i ten sam numer wystawiony ponownie albo z kilkoma zleceniami w TMS. W TMS ani w obsadzeniu nic nie zmieniamy.';

/** Nagłówek karty: anulacja, sama zmiana pojazdu albo zmiana w TMS. */
export function tmsAlertTitle(alerts: readonly Pick<TmsAlert, 'kind' | 'confirmedByTms' | 'source'>[]): string {
  if (alerts.some((a) => a.kind === 'cancellation' && a.source === 'transporeon')) return TRANSPOREON_CANCELLED_TITLE;
  if (alerts.length > 0 && alerts.every((a) => a.source === 'transporeon')) return TRANSPOREON_CHANGE_TITLE;
  if (alerts.some((a) => a.kind === 'cancellation' && a.confirmedByTms)) return TMS_CANCELLED_TITLE;
  if (alerts.some((a) => a.kind === 'cancellation')) return 'Anulacja w TMS — do potwierdzenia';
  if (alerts.length > 0 && alerts.every((a) => a.kind === 'vehicleChange')) {
    return 'Zmiana pojazdu w TMS po obsadzeniu';
  }
  return 'Zmiana w TMS po obsadzeniu';
}

/** Stopka, gdy nikt nie podejmuje decyzji. */
export const TMS_ALERT_NOTE =
  'Obsadzenie i warianty zostają bez zmian. Szczegóły znajdziesz w historii zmian.';

/**
 * Wyjaśnienie pod alertem („dlaczego to zlecenie śledzimy”) z dziennika.
 * „Zlecenie obsadzone naszym pojazdem” nic nie wnosi — karta pokazuje
 * obsadzenie wyżej — więc go nie powtarzamy; resztę pokazujemy zdaniem.
 */
export function tmsAlertReasonText(reason: string | null): string | null {
  if (!reason || /^zlecenie obsadzone naszym (?:pojazdem|autem)$/.test(reason.trim())) {
    return null;
  }
  return startsLowercase(reason) ? asSentence(reason) : reason;
}

/**
 * Stare zdanie o zdjętym obsadzeniu (wpisy sprzed poprawy brzmienia API,
 * 01.10.2026): „W TMS zdjęto obsadzenie (pojazd „X” → brak; status A → B).
 * TMS nie oznacza anulacji — sprawdź u klienta, czy transport jest
 * anulowany, i potwierdź” → brzmienie z STYL-PL. Inne teksty bez zmian.
 */
const LEGACY_CANCELLATION =
  /^W TMS zdjęto obsadzenie \((.*)\)\. TMS nie oznacza anulacji — sprawdź u klienta, czy transport jest anulowany, i potwierdź\.?$/;

export function tmsCancellationText(text: string): string {
  const legacy = LEGACY_CANCELLATION.exec(text);
  if (!legacy) return text;
  const facts = splitOutsideParens(legacy[1]);
  const vehicle = facts.map((f) => /^pojazd „(.*)” → brak$/.exec(f)?.[1]).find(Boolean);
  const status = facts.map((f) => /^status (.+)$/.exec(f)?.[1]).find(Boolean);
  const what = vehicle ? `W TMS usunięto przypisanie pojazdu ${vehicle}` : 'W TMS zdjęto obsadzenie';
  return (
    `${what}${status ? ` (status: ${status})` : ''}. TMS nie oznacza anulacji wprost — ` +
    'sprawdź u zleceniodawcy, czy transport jest odwołany, i potwierdź poniżej.'
  );
}

/**
 * Linie karty: zmiana w TMS to lista zmian po „; ” — każda zmiana osobnym
 * punktem, wyjaśnienie pod ostatnim. Zdjęte obsadzenie zostaje jednym
 * zdaniem (w nawiasie też stoi „; ”).
 */
export function tmsAlertLines(alerts: readonly TmsAlert[]): OrderAlertLine[] {
  return alerts.flatMap((a): OrderAlertLine[] => {
    const detail = tmsAlertReasonText(a.reason);
    // Zmiana pojazdu: API daje zdanie; wpis surowy (JSON) też pokazujemy zdaniem.
    if (a.kind === 'vehicleChange') return [{ id: a.id, text: vehicleChangeAlertText(a.text), detail }];
    if (a.kind !== 'change') {
      // Podejrzenie, które TMS potwierdził listą anulowanych — dopisek; sam
      // wpis z listy anulowanych mówi to w swoim zdaniu.
      const confirmed =
        a.confirmedByTms && !a.text.startsWith('Zlecenie anulowane w TMS') ? TMS_CONFIRMED_NOTE : null;
      return [
        {
          id: a.id,
          text: tmsCancellationText(a.text),
          detail: confirmed && detail ? `${confirmed} ${detail}` : (confirmed ?? detail),
        },
      ];
    }
    const points = splitOutsideParens(a.text).map(capitalize);
    if (points.length === 0) return [{ id: a.id, text: a.text, detail }];
    return points.map((text, i) => ({
      id: `${a.id}:${i}`,
      text,
      detail: i === points.length - 1 ? detail : null,
    }));
  });
}

/** Stopka w trakcie decyzji o anulacji — co się stanie po zapisie. */
const DECISION_NOTE: Record<Decision, string> = {
  confirm:
    'Zlecenie straci przypisany pojazd i wszystkie warianty, a jego stan zmieni się na „Nieaktualne”. Do TMS nic nie wysyłamy.',
  dismiss: 'Obsadzenie, warianty i stan zlecenia zostają bez zmian — zapisujemy tylko Twoją decyzję.',
};

export const TmsChangeAlerts = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [alerts, setAlerts] = useState<TmsAlert[]>([]);
  const [busy, setBusy] = useState(false);
  const [decision, setDecision] = useState<Decision | null>(null);
  const [reason, setReason] = useState('');
  const id = record?.id;
  // Przełączniki funkcji (12.10.2026): bez „Ważnych alertów” karta jak na
  // produkcji (pomarańczowa, czerwona tylko przy anulacji); dymek mówi
  // o zmianie pojazdu i innym pojeździe w TMS tylko przy ich przełącznikach.
  const importantOn = useFeature('importantAlerts');
  const vehicleChanges = useFeature('vehicleChangesFromTms');
  const vehicleSwap = useFeature('vehicleSwapFromTms');
  const cancellations = useFeature('tmsCancellations');

  const load = useCallback(async () => {
    if (id === undefined || id === null) return;
    try {
      // Alert o przeplanowaniu ma własną kartę (`ReplanAlerts`) — tu tylko
      // zmiany z TMS, żeby „widziałem” nie potwierdzało czegoś, czego nie pokazano.
      setAlerts((await fetchTmsAlerts(id)).filter((a) => a.kind !== 'replan'));
    } catch {
      // Błąd odczytu chowa alert — karta działa dalej, jak przy dzwonku.
      setAlerts([]);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (alerts.length === 0 || id === undefined || id === null) return null;

  const cancellation = alerts.some((a) => a.kind === 'cancellation');
  const alertIds = alerts.map((a) => a.id);
  const run = async (action: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await action();
      notify(done, { type: 'success' });
      setDecision(null);
      setReason('');
      await load();
      refresh();
    } catch (err) {
      notify(toErrorMessage(err), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };
  const acknowledge = () =>
    run(() => acknowledgeTmsAlerts(id, alertIds), 'Odnotowano w historii zlecenia');
  const decide = () =>
    decision === 'confirm'
      ? run(
          () => confirmTmsCancellation(id, alertIds, reason),
          'Anulacja potwierdzona. Pojazd i warianty zwolnione, zlecenie oznaczone jako nieaktualne.',
        )
      : run(
          () => dismissTmsCancellation(id, alertIds, reason),
          'Zapisano, że to nie anulacja. Plan bez zmian.',
        );
  const back = () => {
    setDecision(null);
    setReason('');
  };
  const reasonTooShort = reason.trim().length < MIN_DECISION_REASON_LENGTH;

  const actions =
    decision === null ? (
      <>
        {cancellation && (
          <>
            <Button
              variant="contained"
              color="error"
              disableElevation
              sx={ALERT_BUTTON_SX}
              disabled={busy}
              onClick={() => setDecision('confirm')}
            >
              Potwierdzam anulację
            </Button>
            <Button
              variant="outlined"
              sx={ALERT_BUTTON_SX}
              disabled={busy}
              onClick={() => setDecision('dismiss')}
            >
              To nie anulacja
            </Button>
          </>
        )}
        <Button
          variant="contained"
          disableElevation
          startIcon={<CheckRoundedIcon />}
          sx={ALERT_BUTTON_SX}
          disabled={busy}
          onClick={() => void acknowledge()}
        >
          Przyjmuję do wiadomości
        </Button>
      </>
    ) : (
      <>
        <Button color="inherit" sx={ALERT_BUTTON_SX} disabled={busy} onClick={back}>
          Wróć
        </Button>
        <Button
          variant="contained"
          color={decision === 'confirm' ? 'error' : 'primary'}
          disableElevation
          sx={ALERT_BUTTON_SX}
          disabled={busy || reasonTooShort}
          onClick={() => void decide()}
        >
          {decision === 'confirm' ? 'Zwolnij pojazd i zamknij zlecenie' : 'Zapisz, że to nie anulacja'}
        </Button>
      </>
    );

  return (
    <OrderAlertCard
      // Każdy alert z TMS jest ważny (12.10.2026) — czerwień, nie pomarańcz.
      tone={importantOn || cancellation ? 'error' : 'warning'}
      title={tmsAlertTitle(alerts)}
      detectedAt={alerts.map((a) => a.at)}
      origin={
        tmsAlertOrigin({ vehicleChanges, vehicleSwap, cancellations }) +
        (alerts.some((a) => a.source === 'transporeon') ? TRANSPOREON_ALERT_ORIGIN : '')
      }
      lines={tmsAlertLines(alerts)}
      note={decision === null ? TMS_ALERT_NOTE : DECISION_NOTE[decision]}
      actions={actions}
    >
      {decision !== null && (
        <Box sx={{ mt: 1.5 }}>
          <TextField
            label={decision === 'confirm' ? 'Powód potwierdzenia anulacji' : 'Dlaczego to nie anulacja'}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            size="small"
            autoFocus
            sx={{ width: '100%' }}
            helperText={`Co najmniej ${MIN_DECISION_REASON_LENGTH} znaków. Powód zapiszemy w historii zlecenia.`}
          />
        </Box>
      )}
    </OrderAlertCard>
  );
};
